import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PendingReviewCard from '../PendingReview/PendingReviewCard';
import PendingReviewList from '../PendingReview/PendingReviewList';
import PendingReviewEmptyState from '../PendingReview/PendingReviewEmptyState';
import { PENDING_REVIEW_VIEW, PROPERTIES_STATUS } from '../../constants';
import { mapPendingReviewItem } from '../PendingReview/utils';
import { getPropertiesSearchParams } from '../utils';
import { LISTING_EDITION_REVIEW } from '@/components/Developments/constants';
import Trans from '@/components/shared/Trans';
import translations from '../../Dashboard.trans.json';
import { getPropertiesPendingReview } from '@/store/slices/components/dashboard/service';
import useFetchDashboard from '../../customHooks/useFetchDashboard';
import { useTrackPendingTab } from '../../customHooks/useTrackDashboard';
import PropertyIcon from '@/public/images/dashboard/property.svg';

function PropertiesPendingReview() {
  const dispatch = useDispatch();
  const track = useTrackPendingTab();
  const [viewMode, setViewMode] = useState(PENDING_REVIEW_VIEW.NEW);
  const pendingReview = useSelector(
    (state) => state.DashboardSlice.propertiesPendingReview
  );
  const { loading, hasFetched } = pendingReview;
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { count, empty, items } = pendingReview[viewMode] ?? {};
  const { data: userData } = useSelector((state) => state.UserSlice);
  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();
  const mappedItems = (items ?? []).map(mapPendingReviewItem);

  useFetchOnViewport(
    (query) => {
      dispatch(getPropertiesPendingReview({ query }));
    },
    [],
    { hasFetched }
  );

  const handleItemClick = (item) => {
    if (!item?.id) return;

    // New listings have no change request to review yet: they go to the
    // property's quote info section in tokkobroker.
    if (viewMode === PENDING_REVIEW_VIEW.NEW) {
      const tokkoBrokerHost = localStorage.getItem('tokkoBrokerHost');
      window.open(
        `${tokkoBrokerHost}/property/${item.id}#quote_info`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }

    window.open(
      `/change-requests?property=${item.id}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleViewModeChange = (nextViewMode) => {
    setViewMode(nextViewMode);
  };

  const tokkoBrokerHost = localStorage.getItem('tokkoBrokerHost');
  const statusId =
    viewMode === PENDING_REVIEW_VIEW.MODIFIED
      ? PROPERTIES_STATUS.AVAILABLE
      : PROPERTIES_STATUS.APPRAISAL;
  const searchParams = getPropertiesSearchParams({
    statusId,
    listing_edition_review: [LISTING_EDITION_REVIEW.PENDING],
    selectedIds,
  });
  const showAllUrl = `${tokkoBrokerHost}/properties/?search_options=${searchParams}`;

  return (
    <PendingReviewCard
      cardRef={ref}
      loading={loading || isStale}
      empty={empty}
      title={Trans(translations, 'PropertiesPendingReview')}
      count={count}
      viewMode={viewMode}
      onViewModeChange={handleViewModeChange}
      redirectUrl={showAllUrl}
      trackCallback={(element) => track.propertiesPendingReview(element)}
      locale={userData?.user_language}
      emptyState={
        <PendingReviewEmptyState
          icon={PropertyIcon}
          titleKey="NoPropertiesPendingReview"
          descriptionKey="PropertiesPendingReviewEmptyDescription"
        />
      }
    >
      <PendingReviewList
        items={mappedItems}
        viewMode={viewMode}
        entityType="property"
        onItemClick={handleItemClick}
        trackCallback={(element) => track.propertiesPendingReview(element)}
      />
    </PendingReviewCard>
  );
}

export default PropertiesPendingReview;
